import { Link } from 'react-router-dom'
import { Facebook, Instagram, Youtube } from 'lucide-react'
import { useClub } from '@/lib/hooks'
import { Wordmark } from '@/components/brand/Wordmark'

export function Footer() {
  const { data: club } = useClub()
  if (!club) return null
  return (
    <footer className="bg-pitch-950 text-cream-100/80 pt-16 pb-6">
      <div className="container-narrow">
        <div className="grid gap-10 md:grid-cols-4">
          <div>
            <div className="text-white mb-4">
              <Wordmark size="md" />
            </div>
            <p className="text-sm leading-relaxed max-w-xs mb-5">
              Firing the spirit of fairplay and football since {club.since}. A {club.status} youth soccer club in {club.address.city}, {club.address.state}.
            </p>
            <div className="flex gap-2">
              <a href={club.socials.facebook} className="grid h-10 w-10 place-items-center rounded-lg bg-white/6 hover:bg-cosmos-green hover:text-pitch-950 transition-colors" aria-label="Facebook">
                <Facebook className="h-4 w-4" />
              </a>
              <a href={club.socials.instagram} className="grid h-10 w-10 place-items-center rounded-lg bg-white/6 hover:bg-cosmos-green hover:text-pitch-950 transition-colors" aria-label="Instagram">
                <Instagram className="h-4 w-4" />
              </a>
              <a href={club.socials.youtube} className="grid h-10 w-10 place-items-center rounded-lg bg-white/6 hover:bg-cosmos-green hover:text-pitch-950 transition-colors" aria-label="YouTube">
                <Youtube className="h-4 w-4" />
              </a>
            </div>
          </div>

          <FooterCol
            title="Programs"
            links={[
              { to: '/programs/recreation', label: 'Recreation' },
              { to: '/programs/competitive', label: 'Competitive' },
              { to: '/programs/academy', label: 'Academy' },
              { to: '/programs/elite', label: 'Elite' },
              { to: '/programs/international-tours', label: 'International Tours' },
              { to: '/programs/jamaica', label: 'Jamaica Program' },
            ]}
          />

          <FooterCol
            title="Explore"
            links={[
              { to: '/about', label: 'About Us' },
              { to: '/teams', label: 'Teams' },
              { to: '/camps', label: 'Camps' },
              { to: '/coaches', label: 'Staff & Coaching' },
              { to: '/gallery', label: 'Gallery' },
              { to: '/uniforms', label: 'Uniforms' },
              { to: '/sponsors', label: 'Sponsors & Donors' },
            ]}
          />

          <div>
            <h5 className="font-display uppercase tracking-wider text-white text-sm mb-4">Contact</h5>
            <ul className="text-sm space-y-2">
              {club.phones.map((p) => (
                <li key={p.value}>
                  <a href={`tel:${p.value.replace(/[^0-9]/g, '')}`} className="hover:text-cosmos-green transition-colors">
                    {p.value}
                  </a>
                </li>
              ))}
              {club.emails.map((e) => (
                <li key={e.value}>
                  <a href={`mailto:${e.value}`} className="hover:text-cosmos-green transition-colors">
                    {e.value}
                  </a>
                </li>
              ))}
              <li className="pt-1">
                {club.address.line1}, {club.address.city}, {club.address.state} {club.address.zip}
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-col md:flex-row justify-between gap-4 pt-6 border-t border-white/10 text-xs text-white/50">
          <div>© {club.since} – 2026 {club.name}. All rights reserved. {club.status}.</div>
          <div>{club.tagline}</div>
        </div>
      </div>
    </footer>
  )
}

function FooterCol({ title, links }: { title: string; links: { to: string; label: string }[] }) {
  return (
    <div>
      <h5 className="font-display uppercase tracking-wider text-white text-sm mb-4">{title}</h5>
      <ul className="space-y-1.5 text-sm">
        {links.map((l) => (
          <li key={l.to}>
            <Link to={l.to} className="hover:text-cosmos-green transition-colors">
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}
